import { extractTextForTTS } from 'utils/audioUtils'

// The list also cuts the preview to one line; this only keeps the text short.
const MAX_PREVIEW_LENGTH = 140
const HORIZONTAL_RULE = /^[ \t]*(?:[-*_][ \t]*){3,}$/
const LIST_MARK = /^[ \t]*(?:[-*+]|\d+[.)])[ \t]+/
const HEADING = /^[ \t]*#{1,6}[ \t]/
// A block that already ends in punctuation (maybe inside quotes or brackets) reads on with a space.
const ENDS_WITH_PUNCTUATION = /[.!?:;,…。！？：；，、؟؛،]["'”’»)\]]*$/
const BLOCK_SEPARATOR = ' · '

/**
 * A thread's last message as a short line of plain text for the thread list. Messages are
 * Markdown: the marks (bold, headings, list bullets, links, code) and any HTML tags are dropped.
 * Headings, paragraphs and list items are joined with " · " unless the one before already ends
 * in punctuation; line breaks inside a paragraph become spaces.
 */
export const toThreadPreview = (content?: string | null): string => {
  if (!content) return ''
  const blocks: string[] = []
  let continuesBlock = false // a plain next line runs on in the last paragraph or list item
  content.split(/\r?\n/).forEach((line) => {
    if (!line.trim() || HORIZONTAL_RULE.test(line)) {
      continuesBlock = false
    } else if (continuesBlock && !HEADING.test(line) && !LIST_MARK.test(line)) {
      blocks[blocks.length - 1] += `\n${line}`
    } else {
      blocks.push(line.replace(LIST_MARK, ''))
      continuesBlock = !HEADING.test(line)
    }
  })
  const text = blocks
    .map((block) => extractTextForTTS(block))
    .filter(Boolean)
    .reduce((preview, block) => {
      if (!preview) return block
      return `${preview}${ENDS_WITH_PUNCTUATION.test(preview) ? ' ' : BLOCK_SEPARATOR}${block}`
    }, '')
  // Cut by code point, so an emoji is never split into half a surrogate pair; a cut right
  // after a separator drops it too.
  const chars = Array.from(text)
  if (chars.length <= MAX_PREVIEW_LENGTH) return text
  const cut = chars
    .slice(0, MAX_PREVIEW_LENGTH)
    .join('')
    .replace(/[\s·]+$/, '')
  return `${cut}…`
}
